import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminBlogShell,
  AdminListPagination,
  AdminListToolbar,
  AdminPageHeader,
  appendYowanContentPreviewQuery,
  sortAdminOrderedRows,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import {
  fetchYowanAdminReviewsList,
  formatYowanAdminDateTime,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const REVIEW_PAGE_SIZE = 30;
const reviewSortOptions = [
  { value: 'display-order-asc', label: '表示順が小さい順' },
  { value: 'display-order-desc', label: '表示順が大きい順' },
  { value: 'published-status', label: '公開ステータス順' },
  { value: 'title-asc', label: 'タイトル昇順' },
  { value: 'title-desc', label: 'タイトル降順' },
];

const reviewTitleColumnStyle = {
  width: '17rem',
  minWidth: '17rem',
};

const reviewPersonColumnStyle = {
  width: '10rem',
  minWidth: '10rem',
  whiteSpace: 'nowrap',
};

const reviewStatusColumnStyle = {
  width: '8rem',
  minWidth: '8rem',
  whiteSpace: 'nowrap',
};

const reviewOrderColumnStyle = {
  width: '5.5rem',
  minWidth: '5.5rem',
  whiteSpace: 'nowrap',
};

const reviewDateColumnStyle = {
  width: '11.5rem',
  minWidth: '11.5rem',
  whiteSpace: 'nowrap',
};

const reviewActionsColumnStyle = {
  width: '6rem',
  minWidth: '6rem',
  whiteSpace: 'nowrap',
};

const reviewTableStyle = {
  minWidth: '86rem',
};

const AdminReviewsListPage = () => {
  const content = yowanPageContent['/admin/content/reviews'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState('display-order-asc');
  const [currentPage, setCurrentPage] = useState(1);

  React.useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setLoadError('');

    fetchYowanAdminReviewsList()
      .then((payload) => {
        if (isMounted) {
          setRows(payload.results);
        }
      })
      .catch(() => {
        if (isMounted) {
          setRows([]);
          setLoadError('一覧を読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  if (!content) {
    return null;
  }

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredRows = normalizedQuery
    ? rows.filter((row) =>
        [
          row.title,
          row.reviewerName,
          row.reviewerTitle,
          row.body,
          row.isPublished ? '公開中' : '非公開',
          String(row.displayOrder),
        ]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(normalizedQuery)),
      )
    : rows;
  const sortedRows = sortAdminOrderedRows(filteredRows, sortKey);
  const totalPages = Math.max(1, Math.ceil(sortedRows.length / REVIEW_PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * REVIEW_PAGE_SIZE;
  const visibleRows = sortedRows.slice(startIndex, startIndex + REVIEW_PAGE_SIZE);

  return (
    <AdminBlogShell activeNavHref="/admin/content/reviews" routeKey="/admin/content/reviews">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link
            key="primary-action"
            className="c-form__submit"
            to={appendYowanContentPreviewQuery(content.primaryAction.href, preservedDomainSearch)}
          >
            {content.primaryAction.label}
          </Link>,
        ].filter(Boolean)}
      />

      <AdminListToolbar
        searchValue={searchQuery}
        onSearchChange={(event) => {
          setSearchQuery(event.target.value);
          setCurrentPage(1);
        }}
        sortValue={sortKey}
        onSortChange={(event) => {
          setSortKey(event.target.value);
          setCurrentPage(1);
        }}
        metaText={`${sortedRows.length}件中 ${visibleRows.length}件を表示${
          totalPages > 1 ? ` / ${safeCurrentPage} / ${totalPages}ページ` : ''
        }`}
        searchPlaceholder="タイトル / 氏名 / 肩書き / 本文 / 表示順 / 公開"
        sortOptions={reviewSortOptions}
      />

      <div className="yowan-admin-resource-table-wrap c-surface-content">
        <table className="yowan-admin-resource-table" style={reviewTableStyle}>
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col" style={reviewTitleColumnStyle}>タイトル</th>
              <th scope="col" style={reviewPersonColumnStyle}>氏名</th>
              <th scope="col" style={reviewPersonColumnStyle}>肩書き</th>
              <th scope="col" style={reviewStatusColumnStyle}>公開</th>
              <th scope="col" style={reviewOrderColumnStyle}>表示順</th>
              <th scope="col" style={reviewDateColumnStyle}>作成日</th>
              <th scope="col" style={reviewDateColumnStyle}>更新日</th>
              <th scope="col" style={reviewActionsColumnStyle}>操作</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, index) => (
              <tr key={row.id}>
                <td>{startIndex + index + 1}</td>
                <td style={reviewTitleColumnStyle}>
                  <p className="yowan-admin-resource-table__title">{row.title}</p>
                </td>
                <td style={reviewPersonColumnStyle}>{row.reviewerName || '-'}</td>
                <td style={reviewPersonColumnStyle}>{row.reviewerTitle || '-'}</td>
                <td style={reviewStatusColumnStyle}>
                  <span
                    className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${
                      row.isPublished ? 'published' : 'draft'
                    }`}
                  >
                    {row.isPublished ? '公開中' : '非公開'}
                  </span>
                </td>
                <td style={reviewOrderColumnStyle}>{row.displayOrder}</td>
                <td style={reviewDateColumnStyle}>{formatYowanAdminDateTime(row.createdAt)}</td>
                <td style={reviewDateColumnStyle}>{formatYowanAdminDateTime(row.updatedAt)}</td>
                <td style={reviewActionsColumnStyle}>
                  <div className="yowan-admin-resource-table__actions">
                    <Link to={appendYowanContentPreviewQuery(row.editHref, preservedDomainSearch)}>編集</Link>
                  </div>
                </td>
              </tr>
            ))}
            {!visibleRows.length ? (
              <tr>
                <td colSpan="9">
                  {isLoading ? <LoadingSpinner size="sm" /> : loadError || '表示できるレコードがありません。'}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <AdminListPagination
        currentPage={safeCurrentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </AdminBlogShell>
  );
};

export default AdminReviewsListPage;
